<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import Button from '@/components/ds/Button.vue';
import ListPager from '@/components/panels/ListPager.vue';
import { useListPage } from '@/composables/useListPage.js';

const props = defineProps({
  appId: { type: String, required: true },
  fingerprint: { type: String, required: true },
  window: { type: String, required: true },
});
const state = ref('loading');
const result = ref(null);
const cursor = ref(null);
const copyStatus = ref('');
const rows = computed(() => result.value?.occurrences ?? []);
const { page, visible, pageSize } = useListPage(rows, 5);
let controller;
let version = 0;
async function load(nextCursor = null) {
  controller?.abort();
  controller = new AbortController();
  const current = ++version;
  cursor.value = nextCursor;
  state.value = 'loading';
  result.value = null;
  copyStatus.value = '';
  page.value = 1;
  try {
    const response = await api.events.errorDetails({ ...props, cursor: nextCursor, signal: controller.signal });
    if (!response?.ok || !Array.isArray(response.occurrences)) throw new Error('Invalid details response');
    if (current !== version) return;
    result.value = response;
    state.value = 'data';
  } catch {
    if (current === version) state.value = 'error';
  }
}
watch(() => [props.appId, props.fingerprint, props.window], () => load(), { immediate: true });
onBeforeUnmount(() => { version++; controller?.abort(); });
async function copy(value) {
  const current = version;
  try {
    await navigator.clipboard.writeText(value);
    if (current === version) copyStatus.value = 'Copied.';
  } catch {
    if (current === version) copyStatus.value = 'Could not copy. Select and copy the trace manually.';
  }
}
function date(value) { return new Date(value).toLocaleString(); }
</script>

<template>
  <section
    class="error-technical"
    aria-label="Technical error details"
    :aria-busy="state === 'loading'"
  >
    <h3>Technical details</h3>
    <p class="error-technical__hint">
      Recorded data only; messages and traces may be truncated or redacted.
      Traces are raw, not translated with source maps. Component and action are reported context, not a confirmed cause.
    </p>
    <p
      v-if="state === 'loading'"
      role="status"
    >
      Loading technical details…
    </p>
    <div
      v-else-if="state === 'error'"
      role="alert"
    >
      <p>Could not load technical details.</p>
      <Button
        variant="secondary"
        size="sm"
        @click="load(cursor)"
      >
        Retry technical details
      </Button>
    </div>
    <template v-else>
      <h4>Group sample stack</h4>
      <template v-if="result.sampleStack">
        <pre
          class="error-technical__trace"
          tabindex="0"
          aria-label="Group sample stack"
        >{{ result.sampleStack }}</pre>
        <Button
          variant="secondary"
          size="sm"
          @click="copy(result.sampleStack)"
        >
          Copy sample stack
        </Button>
      </template>
      <p v-else>
        No separate stack was recorded for this group. Older promise rejections may include it in their recorded reason.
      </p>
      <h4>Retained occurrences in this period</h4>
      <p class="error-technical__hint">
        {{ rows.length }} matching events in this batch of {{ result.scanned }} inspected events.
        Historical totals can exceed retained events. Matching uses current module rules.
      </p>
      <p v-if="!rows.length">
        No matching occurrences in this batch.
      </p>
      <details
        v-for="event in visible"
        :key="event.eventId"
        class="error-technical__event"
      >
        <summary>{{ date(event.occurredAt) }} · {{ event.pagePath || 'No page recorded' }}</summary>
        <dl class="error-technical__fields">
          <div><dt>Event</dt><dd>{{ event.eventId }}</dd></div>
          <div><dt>Source</dt><dd>{{ event.source || 'Not recorded' }}</dd></div>
          <div><dt>Line / column</dt><dd>{{ event.line ?? '—' }} / {{ event.column ?? '—' }}</dd></div>
          <div><dt>Component</dt><dd>{{ event.component || 'Not recorded' }}</dd></div>
          <div><dt>Action / element</dt><dd>{{ event.action || 'Not recorded' }} / {{ event.selector || 'Not recorded' }}</dd></div>
          <div><dt>Release</dt><dd>{{ event.releaseSha || 'Not recorded' }}</dd></div>
          <div><dt>Environment</dt><dd>{{ event.environment || 'Unknown (legacy or unlabelled)' }}</dd></div>
          <div v-if="event.method">
            <dt>Request</dt><dd>{{ event.method }} · status {{ event.status ?? '—' }} · {{ event.durationMs ?? '—' }} ms · {{ event.errorKind || '—' }}</dd>
          </div>
        </dl>
        <h4>Recorded message / reason</h4>
        <pre class="error-technical__trace">{{ event.reason || event.message || 'No message recorded' }}</pre>
        <template v-if="event.stack">
          <h4>Occurrence stack</h4>
          <pre
            class="error-technical__trace"
            tabindex="0"
            aria-label="Occurrence stack"
          >{{ event.stack }}</pre>
          <Button
            variant="secondary"
            size="sm"
            @click="copy(event.stack)"
          >
            Copy occurrence stack
          </Button>
        </template>
        <p v-else>
          No separate stack recorded for this occurrence.
        </p>
        <a
          v-if="event.sessionId"
          :href="`/apps/${encodeURIComponent(appId)}/sessions/${encodeURIComponent(event.sessionId)}`"
        >Open session</a>
        <p v-else>
          No session recorded.
        </p>
      </details>
      <ListPager
        v-model:page="page"
        :total="rows.length"
        :page-size="pageSize"
        label="Error occurrences pagination"
      />
      <div class="error-technical__actions">
        <Button
          v-if="cursor"
          variant="secondary"
          size="sm"
          @click="load()"
        >
          Latest batch
        </Button>
        <Button
          v-if="result.nextCursor"
          variant="secondary"
          size="sm"
          @click="load(result.nextCursor)"
        >
          Inspect older events
        </Button>
      </div>
    </template>
    <p role="status">
      {{ copyStatus }}
    </p>
  </section>
</template>

<style scoped>
.error-technical {
  min-width: 0;
  padding-block: var(--space-3);
}

.error-technical__hint {
  color: var(--fg-muted);
  font-size: var(--text-sm);
}

.error-technical__trace {
  max-height: 20rem;
  overflow: auto;
  padding: var(--space-3);
  background: var(--bg-canvas);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: var(--font-mono);
  font-size: var(--text-sm);
}

.error-technical__event {
  padding: var(--space-3);
  margin-block: var(--space-2);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  overflow-wrap: anywhere;
}

.error-technical__event summary {
  cursor: pointer;
}

.error-technical__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 14rem), 1fr));
  gap: var(--space-3);
}

.error-technical__fields dt {
  color: var(--fg-muted);
}

.error-technical__fields dd {
  margin: 0;
}

.error-technical__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
</style>
